import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "@studio-freight/lenis";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, AuthProvider, useLanguage, useAuth } from "@/context/AppProviders";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Formation from "@/pages/Formation";
import AuthPage from "@/pages/AuthPage";
import MemberArea from "@/pages/MemberArea";
import Temoignages from "@/pages/Temoignages";
import ResetPassword from "@/pages/ResetPassword";
import ConfirmEmail from "@/pages/ConfirmEmail";
import Profile from "@/pages/Profile";
import ForceChangePassword from "@/pages/ForceChangePassword";
import Admin from "@/pages/Admin";

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) { setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100); return; }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const TitleManager = () => {
  const { pathname } = useLocation();
  const { t } = useLanguage();
  useEffect(() => {
    const map = {
      "/": t.nav.home,
      "/formation": t.nav.formation,
      "/temoignages": t.nav.testimonials,
      "/auth": t.nav.login,
      "/reinitialiser-mot-de-passe": t.auth.resetTitle,
      "/confirmer-email": t.verify.overline,
      "/profil": t.nav.profile,
      "/changer-mot-de-passe": t.forcePw.overline,
      "/espace-membre": t.nav.member,
      "/admin": t.nav.admin,
    };
    const page = map[pathname];
    document.title = page ? `M.SAH - ${page}` : "M.SAH";
  }, [pathname, t]);
  return null;
};

const Layout = ({ children }) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const bare = pathname === "/auth";
  if (user && user.must_change_password && pathname !== "/changer-mot-de-passe") {
    return <ForceChangePassword />;
  }
  return (
    <>
      {!bare && <Navbar />}
      <main>{children}</main>
      {!bare && <Footer />}
    </>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="App bg-paper min-h-screen">
      <div className="grain-overlay" />
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollManager />
            <TitleManager />
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/formation" element={<Formation />} />
                <Route path="/temoignages" element={<Temoignages />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
                <Route path="/confirmer-email" element={<ConfirmEmail />} />
                <Route path="/profil" element={<Profile />} />
                <Route path="/changer-mot-de-passe" element={<ForceChangePassword />} />
                <Route path="/espace-membre" element={<MemberArea />} />
                <Route path="/admin" element={<Admin />} />
              </Routes>
            </Layout>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
